import { Box, Button, Chip, Stack, SvgIcon, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  httpMethodTone,
  semanticTone,
} from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import AgenticEmptyState from 'components/@home/@settings/Tabs/Agents/AgenticEmptyState';
import AttachToolsDialog from 'components/@home/@settings/Tabs/Agents/AttachToolsDialog';
import AgenticPanelToolbar from 'components/@home/@settings/Tabs/Agents/panels/AgenticPanelToolbar';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import { normalizeToolTrigger } from 'constants/agenticToolTriggers';
import CodeTagsIcon from 'mdi-react/CodeTagsIcon';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import WebhookIcon from 'mdi-react/WebhookIcon';
import WrenchOutlineIcon from 'mdi-react/WrenchOutlineIcon';
import { arrayOf, func, object } from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { lifecycleCatalogTools } from 'utils/agentGlobalTools';
import { buildAgenticPath } from 'utils/agenticRoute';
import { attachedSkillsForAgent, unionToolsForAgent } from 'utils/skillToolsUnion';
import { I18n } from 'utils/i18n';

function triggerLabel(trigger) {
  const key = normalizeToolTrigger(trigger);
  return I18n.t(`AgenticChatFlow.Trigger_${key}`, { defaultValue: key });
}

const nameCellSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '2px',
  height: '100%',
  minWidth: 0,
  pt: 1,
  pb: 1,
};

const nameRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  fontWeight: 600,
  color: 'text.primary',
  lineHeight: 1.3,
};

const descriptionSx = {
  fontSize: theme => theme.typography.pxToRem(12),
  color: 'text.secondary',
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  pl: 3,
};

const ToolsPanel = ({ agent, tools, skills = [], onSaveAgent }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { flow, authCompany, onCreateTool } = useAgentEdit();
  const [manageOpen, setManageOpen] = useState(false);

  const { kindChipSx, codeChipSx, skillChipSx, agentChipSx, triggerChipSx } = useMemo(() => {
    const successTone = semanticTone(theme, 'success');
    const primaryTone = semanticTone(theme, 'primary');
    const mutedChip = semanticTone(theme, 'default');
    const kind = {
      alignSelf: 'flex-start',
      height: 24,
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 500,
      borderRadius: '12px',
      '& .MuiChip-label': { padding: '0 8px' },
      '& .MuiChip-icon': { marginLeft: '6px', marginRight: '-2px' },
    };
    return {
      kindChipSx: kind,
      codeChipSx: {
        ...kind,
        backgroundColor: successTone.bg,
        color: successTone.fg,
        '& .MuiChip-icon': { color: successTone.fg },
      },
      skillChipSx: {
        height: 22,
        fontSize: theme.typography.pxToRem(11),
        backgroundColor: mutedChip.bg,
        color: mutedChip.fg,
        '& .MuiChip-label': { padding: '0 8px' },
      },
      agentChipSx: {
        height: 22,
        fontSize: theme.typography.pxToRem(11),
        backgroundColor: primaryTone.bg,
        color: primaryTone.fg,
        '& .MuiChip-label': { padding: '0 8px' },
      },
      triggerChipSx: {
        height: 22,
        fontSize: theme.typography.pxToRem(11),
        backgroundColor: mutedChip.bg,
        color: mutedChip.fg,
        '& .MuiChip-label': { padding: '0 8px' },
      },
    };
  }, [theme]);

  const attachedSkills = useMemo(() => attachedSkillsForAgent(agent, skills), [agent, skills]);

  const rows = useMemo(
    () => unionToolsForAgent(agent, attachedSkills, tools).rows,
    [agent, attachedSkills, tools],
  );

  const lifecycleCatalog = useMemo(() => lifecycleCatalogTools(tools), [tools]);
  const attachments = agent?.attachedTools || [];

  const handleOpenRow = row => {
    if (!flow?._id || !row?._id) return;
    const id = String(row._id);
    navigate(buildAgenticPath(authCompany, { flowId: flow._id, section: 'tools', toolId: id }), {
      state: {
        skillIds: (row.skillsForTool || []).map(s => String(s._id)),
        agentGlobal: Boolean(row.agentGlobal),
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: I18n.t('AgenticChatFlow.Name'),
        flex: 2,
        minWidth: 220,
        valueGetter: (value, row) => row.name,
        renderCell: ({ row }) => (
          <Box sx={nameCellSx}>
            <Box component="span" sx={nameRowSx}>
              <WrenchOutlineIcon size={16} />
              {row.name}
            </Box>
            {row.description ? (
              <Box sx={descriptionSx} title={row.description}>
                {row.description}
              </Box>
            ) : null}
          </Box>
        ),
      },
      {
        field: 'kind',
        headerName: I18n.t('AgenticChatFlow.Type'),
        flex: 0.9,
        minWidth: 150,
        valueGetter: (value, row) => {
          if (row.builtin) return 'builtin';
          return row.kind === 'http' ? `http ${row.http?.method || ''}` : 'code';
        },
        renderCell: ({ row }) => {
          if (row.builtin) {
            return (
              <Chip
                size="small"
                icon={<CodeTagsIcon size={14} />}
                label={I18n.t('AgenticChatFlow.BuiltinTool')}
                sx={codeChipSx}
              />
            );
          }
          if (row.kind === 'http') {
            const method = (row.http?.method || 'POST').toUpperCase();
            const palette = httpMethodTone(theme, method);
            return (
              <Chip
                size="small"
                icon={<WebhookIcon size={14} />}
                label={`HTTP ${method}`}
                sx={{
                  ...kindChipSx,
                  backgroundColor: palette.bg,
                  color: palette.fg,
                  '& .MuiChip-icon': { color: palette.fg },
                }}
              />
            );
          }
          return (
            <Chip
              size="small"
              icon={<CodeTagsIcon size={14} />}
              label={I18n.t('AgenticChatFlow.KindCode')}
              sx={codeChipSx}
            />
          );
        },
      },
      {
        field: 'trigger',
        headerName: I18n.t('AgenticChatFlow.Trigger'),
        flex: 0.8,
        minWidth: 130,
        valueGetter: (value, row) => {
          const t = normalizeToolTrigger(row.trigger);
          return t === 'agent' ? '' : t;
        },
        renderCell: ({ row }) => {
          const t = normalizeToolTrigger(row.trigger);
          if (t === 'agent') return '—';
          return <Chip size="small" label={triggerLabel(t)} sx={triggerChipSx} />;
        },
      },
      {
        field: 'attachedTo',
        headerName: I18n.t('AgenticChatFlow.AttachedTo'),
        flex: 1.5,
        minWidth: 180,
        sortable: false,
        filterable: false,
        valueGetter: (value, row) => {
          const skillsLabel = (row.skillsForTool || [])
            .map(s => s.displayName || s.name)
            .join(', ');
          if (row.agentGlobal && !(row.skillsForTool || []).length) {
            return I18n.t('AgenticChatFlow.AttachedToAgent');
          }
          return skillsLabel;
        },
        renderCell: ({ row }) => {
          const list = row.skillsForTool || [];
          const chips = [];
          if (row.agentGlobal) {
            chips.push(
              <Chip
                key="agent"
                size="small"
                label={I18n.t('AgenticChatFlow.AttachedToAgent')}
                sx={agentChipSx}
              />,
            );
          }
          list.forEach(s => {
            chips.push(
              <Chip key={s._id} size="small" label={s.displayName || s.name} sx={skillChipSx} />,
            );
          });
          if (chips.length === 0) return '—';
          return (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                flexWrap: 'wrap',
              }}
            >
              {chips}
            </Stack>
          );
        },
      },
    ],
    [theme, kindChipSx, codeChipSx, triggerChipSx, agentChipSx, skillChipSx],
  );

  const toolbarActions = (
    <>
      <Button variant="outlined" color="secondary" onClick={() => setManageOpen(true)}>
        <SvgIcon sx={{ fontSize: 18, mr: '5px', color: 'primary.main' }}>
          <CogOutlineIcon />
        </SvgIcon>
        {I18n.t('AgenticChatFlow.ManageTools')}
      </Button>
      {onCreateTool && (
        <Button variant="outlined" color="secondary" onClick={onCreateTool}>
          <SvgIcon sx={{ fontSize: 18, mr: '5px', color: 'primary.main' }}>
            <PlusIcon />
          </SvgIcon>
          {I18n.t('AgenticChatFlow.CreateTool')}
        </Button>
      )}
    </>
  );

  return (
    <Box
      sx={{
        p: 3,
        px: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: 1,
        minHeight: 0,
      }}
    >
      {rows.length === 0 ? (
        <AgenticEmptyState
          concept="tool"
          title={I18n.t('AgenticChatFlow.EmptyTools.Title')}
          lead={I18n.t('AgenticChatFlow.EmptyTools.Lead')}
          secondaryLabel={I18n.t('AgenticChatFlow.ManageTools')}
          secondaryIcon={<CogOutlineIcon size={16} />}
          onSecondary={() => setManageOpen(true)}
          primaryLabel={onCreateTool ? I18n.t('AgenticChatFlow.CreateTool') : null}
          primaryIcon={<PlusIcon size={16} />}
          onPrimary={onCreateTool || undefined}
        />
      ) : (
        <>
          <Typography
            sx={{
              fontSize: t => t.typography.pxToRem(22),
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {I18n.t('AgenticChatFlow.AllTools')}
          </Typography>
          <OmmnioDataGrid
            rows={rows}
            columns={columns}
            columnHeaderHeight={48}
            getRowId={r => r._id}
            rowCount={rows.length}
            autoHeight={false}
            disableEvenOdd
            rowHeight={64}
            defaultSortModel={[{ field: 'name', sort: 'asc' }]}
            onCellClick={cell => handleOpenRow(cell.row)}
            slotProps={{
              toolbar: {
                searchPlaceholder: I18n.t('AgenticChatFlow.Search'),
                children: toolbarActions,
              },
            }}
            slots={{ toolbar: AgenticPanelToolbar }}
            sx={{
              flex: 1,
              minHeight: 320,
              borderRadius: 1,
              '& .MuiDataGrid-cell': { cursor: 'pointer' },
            }}
          />
        </>
      )}

      <AttachToolsDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        tools={lifecycleCatalog}
        selectedAttachments={attachments}
        onSave={next => onSaveAgent({ attachedTools: next })}
        onCreateTool={onCreateTool}
        dialogTitle={I18n.t('AgenticChatFlow.AttachGlobalToolsTitle')}
        catalogBuiltinFilter={b => b.name === 'announce'}
      />
    </Box>
  );
};

ToolsPanel.propTypes = {
  agent: object.isRequired,
  tools: arrayOf(object).isRequired,
  skills: arrayOf(object),
  onSaveAgent: func.isRequired,
};

export default ToolsPanel;
